import { datadogRum } from '@datadog/browser-rum'
import debug from '../utils/console'
import { isDebugEnabled } from '../utils/debug/isDebugEnabled'
import {
  PREBID_AUCTION_EVENTS,
  PREBID_AUCTION_SUMMARY_VITAL,
  PREBID_AUCTION_VITAL,
  createPrebidAuctionTracker,
} from '../utils/ad-ops/prebidAuctions'
import type {
  PrebidAuctionCounts,
  PrebidAuctionEvent,
  PrebidAuctionSample,
  PrebidAuctionTracker,
} from '../utils/ad-ops/prebidAuctions'
import {
  firstAdRequestAt,
  firstAdRequestContext,
} from '../utils/consent/firstAdRequest'
import { markFirstAdStage } from '../utils/ad-ops/firstAdStages'

const logger = debug.withTag('prebid-auction')

// Only the calls read here. `pbjs` is AY's prebid global
// (`window._pbjsGlobals` lists `pbjs`).
interface PrebidGlobal {
  que?: unknown
  onEvent?: unknown
  getEvents?: unknown
}

const PREBID_GLOBAL = 'pbjs'
const EVENTS = new Set<string>(PREBID_AUCTION_EVENTS)

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null
const isAuctionEvent = (name: unknown): name is PrebidAuctionEvent =>
  typeof name === 'string' && EVENTS.has(name)
const prebidGlobal = (): PrebidGlobal | undefined => {
  const pbjs: unknown = Reflect.get(window, PREBID_GLOBAL)
  return isRecord(pbjs) ? pbjs : undefined
}

const isPrerendering = () => {
  const doc: Document & { prerendering?: boolean } = document
  return doc.prerendering === true
}
const isHidden = () =>
  document.visibilityState === 'hidden' && !isPrerendering()

// Zero until a prerendered page is shown.
const activationStart = () => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const navigation = performance.getEntriesByType('navigation')[0] as
    | (PerformanceNavigationTiming & { activationStart?: number })
    | undefined
  return navigation?.activationStart ?? 0
}

const withoutTrailingSlash = (path: string) => path.replace(/\/+$/, '') || '/'

function listen(auctions: PrebidAuctionTracker) {
  const pbjs = prebidGlobal()
  if (!pbjs) return
  // Events from before this point, with the times they happened. A page shown
  // from prerendering only counts what happened once the visitor saw it.
  try {
    const shownAt = activationStart()
    const history: unknown =
      typeof pbjs.getEvents === 'function' ? pbjs.getEvents() : []
    for (const entry of Array.isArray(history) ? history : []) {
      if (!isRecord(entry)) continue
      const { eventType, args, elapsedTime } = entry
      if (
        isAuctionEvent(eventType) &&
        typeof elapsedTime === 'number' &&
        elapsedTime >= shownAt
      ) {
        auctions.handle(eventType, args, elapsedTime)
      }
    }
  } catch (error) {
    logger.warn('Could not replay earlier prebid auctions', error)
  }
  try {
    if (typeof pbjs.onEvent !== 'function') return
    for (const event of PREBID_AUCTION_EVENTS) {
      pbjs.onEvent(event, (args: unknown) =>
        auctions.handle(event, args, performance.now())
      )
    }
  } catch (error) {
    logger.warn('Could not listen to prebid auctions', error)
  }
}

// The standard prebid queue: commands pushed before prebid loads run once it
// does, and right away after.
function whenPrebidReady(command: () => void) {
  let pbjs = prebidGlobal()
  if (!pbjs) {
    pbjs = {}
    Reflect.set(window, PREBID_GLOBAL, pbjs)
  }
  if (!isRecord(pbjs.que)) pbjs.que = []
  const que = pbjs.que
  if (isRecord(que) && typeof que.push === 'function') que.push(command)
}

function startTracking() {
  const debugEnabled = isDebugEnabled()
  const auctions = createPrebidAuctionTracker({
    timeOrigin: performance.timeOrigin,
    intentAt: firstAdRequestAt,
    isHidden,
    send: (sample: PrebidAuctionSample) => {
      if (debugEnabled) logger.info('Prebid auction', sample)
      datadogRum.addDurationVital(PREBID_AUCTION_VITAL, {
        startTime: sample.startTime,
        duration: sample.duration,
        context: { ...sample.context, ...firstAdRequestContext() },
      })
    },
    sendSummary: (sample: PrebidAuctionSample) => {
      if (debugEnabled) logger.info('Prebid auctions of the view', sample)
      datadogRum.addDurationVital(PREBID_AUCTION_SUMMARY_VITAL, {
        startTime: sample.startTime,
        duration: sample.duration,
        context: sample.context,
      })
    },
    // The view's own count of auctions, kept up to date for live inspection.
    // The summary vital carries the final ones.
    onCounts: ({ seen, sent, dropped }: PrebidAuctionCounts) => {
      datadogRum.setViewContextProperty('prebid_auctions_seen', seen)
      datadogRum.setViewContextProperty('prebid_auctions_sent', sent)
      datadogRum.setViewContextProperty('prebid_auctions_dropped', dropped)
    },
    // For the stages on first_ad_visible, see utils/ad-ops/firstAdStages.ts.
    onFirstAuction: (moment, at) =>
      markFirstAdStage(
        moment === 'start' ? 'first-auction-start' : 'first-auction-end',
        at
      ),
  })

  // On the window in the capture phase, registered before RUM starts from
  // app.vue, so the final counts are sent before the SDK flushes on the way
  // out.
  const capture = { capture: true }
  window.addEventListener(
    'visibilitychange',
    () => {
      if (isHidden()) auctions.endView('hidden', performance.now())
    },
    capture
  )
  window.addEventListener(
    'pagehide',
    () => auctions.endView('exit', performance.now()),
    capture
  )

  // Another path is another page, and RUM starts a new view for it. A query
  // change (a category tab) is the same page, as in first-ad-visible. The
  // outgoing view ends only once the navigation went through: a guard or a
  // lazy component can still abort it, and the page stays the same. RUM has
  // moved to the new view by then, but it puts a vital on the view current at
  // its start time, and the summary starts at the outgoing view's start.
  useRouter().afterEach((to, from, failure) => {
    if (
      failure ||
      from.matched.length === 0 ||
      withoutTrailingSlash(to.path) === withoutTrailingSlash(from.path)
    ) {
      return
    }
    auctions.endView('navigation', performance.now())
    auctions.startView(performance.now())
  })

  const register = () => whenPrebidReady(() => listen(auctions))
  if (isPrerendering()) {
    document.addEventListener('prerenderingchange', register, { once: true })
  } else {
    register()
  }
}

/**
 * Sends a `prebid_auction` summary for each of the first few prebid auctions
 * of every view. See utils/ad-ops/prebidAuctions.ts.
 *
 * Starts whether prebid loads before or after it, and RUM holds the vitals
 * until it starts from the tenant's app.vue. Read only: it listens to prebid's
 * events and never calls anything that changes an auction. Nothing is sent
 * when the tenant has no RUM configured, and a page that loaded in a
 * background tab reports nothing, since its auctions would be about the
 * visitor and not about the page.
 */
export default defineNuxtPlugin({
  name: 'prebid-auction',
  setup() {
    const config = useRuntimeConfig()
    if (
      !config.public.datadogRumApplicationId ||
      !config.public.datadogRumClientToken
    ) {
      return
    }
    if (isHidden()) return

    // Only a measurement, so a failure here must never take the page down.
    try {
      startTracking()
    } catch (error) {
      logger.warn('Could not start summarizing prebid auctions', error)
    }
  },
})
